import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { toast } from "sonner";
import i18n from "@/i18n";

/** Item fiscal profile (DB + NFC-e 2026 / IBS-CBS fields). */
export interface ItemFiscalProfile {
  id: string;
  item_id: string;
  customer_id: string;
  gtin: string | null;
  ncm: string | null;
  cest: string | null;
  cfop: string | null;
  cfop_outside_state: string | null;
  origin_code: string | null;
  icms_tax_situation: string | null;
  pis_tax_situation: string | null;
  cofins_tax_situation: string | null;
  ipi_tax_situation: string | null;
  ipi_enquadramento: string | null;
  ipi_base_calculo: number | null;
  ipi_aliquota: number | null;
  ipi_valor: number | null;
  nfse_service_list_item: string | null;
  nfse_service_code: string | null;
  nfse_cnae: string | null;
  nfse_iss_rate: number | null;
  nfse_service_description: string | null;
  nfse_natureza_operacao: string | null;
  nfse_regime_especial_tributacao: string | null;
  nfse_optante_simples_nacional: boolean | null;
  nfse_iss_retido: boolean | null;
  nfse_base_calculo: number | null;
  nfse_valor_iss: number | null;
  nfse_valor_deducoes: number | null;
  nfse_desconto_incondicionado: number | null;
  nfse_desconto_condicionado: number | null;
  nfse_valor_pis: number | null;
  nfse_valor_cofins: number | null;
  nfse_ibs_cbs_situacao_tributaria: string | null;
  nfse_ibs_cbs_classificacao_tributaria: string | null;
  nfse_ibs_cbs_base_calculo: number | null;
  nfse_ibs_uf_aliquota: number | null;
  nfse_ibs_mun_aliquota: number | null;
  nfse_cbs_aliquota: number | null;
  nfse_ibs_uf_valor: number | null;
  nfse_ibs_mun_valor: number | null;
  nfse_cbs_valor: number | null;
  created_at: string;
  updated_at: string;
  // NFC-e 2026: tributável e impostos detalhados
  unidade_tributavel: string | null;
  valor_total_tributos: number | null;
  aliquota_aproximada_tributos: number | null;
  fonte_valor_total_tributos: string | null;
  icms_base_calculo: number | null;
  icms_aliquota: number | null;
  icms_valor: number | null;
  icms_modalidade_base_calculo: string | null;
  pis_base_calculo: number | null;
  pis_aliquota: number | null;
  pis_valor: number | null;
  cofins_base_calculo: number | null;
  cofins_aliquota: number | null;
  cofins_valor: number | null;
  ibs_cbs_situacao_tributaria: string | null;
  ibs_cbs_classificacao_tributaria: string | null;
  ibs_cbs_base_calculo: number | null;
  cbs_aliquota: number | null;
  cbs_valor: number | null;
  ibs_uf_aliquota: number | null;
  ibs_uf_valor: number | null;
  ibs_mun_aliquota: number | null;
  ibs_mun_valor: number | null;
  ibs_valor_total: number | null;
  is_valor: number | null;
}

export interface ItemFiscalProfileInput {
  item_id: string;
  gtin?: string | null;
  ncm?: string | null;
  cest?: string | null;
  cfop?: string | null;
  cfop_outside_state?: string | null;
  origin_code?: string | null;
  icms_tax_situation?: string | null;
  pis_tax_situation?: string | null;
  cofins_tax_situation?: string | null;
  ipi_tax_situation?: string | null;
  ipi_enquadramento?: string | null;
  ipi_base_calculo?: number | null;
  ipi_aliquota?: number | null;
  ipi_valor?: number | null;
  nfse_service_list_item?: string | null;
  nfse_service_code?: string | null;
  nfse_cnae?: string | null;
  nfse_iss_rate?: number | null;
  nfse_service_description?: string | null;
  nfse_natureza_operacao?: string | null;
  nfse_regime_especial_tributacao?: string | null;
  nfse_optante_simples_nacional?: boolean | null;
  nfse_iss_retido?: boolean | null;
  nfse_base_calculo?: number | null;
  nfse_valor_iss?: number | null;
  nfse_valor_deducoes?: number | null;
  nfse_desconto_incondicionado?: number | null;
  nfse_desconto_condicionado?: number | null;
  nfse_valor_pis?: number | null;
  nfse_valor_cofins?: number | null;
  nfse_ibs_cbs_situacao_tributaria?: string | null;
  nfse_ibs_cbs_classificacao_tributaria?: string | null;
  nfse_ibs_cbs_base_calculo?: number | null;
  nfse_ibs_uf_aliquota?: number | null;
  nfse_ibs_mun_aliquota?: number | null;
  nfse_cbs_aliquota?: number | null;
  nfse_ibs_uf_valor?: number | null;
  nfse_ibs_mun_valor?: number | null;
  nfse_cbs_valor?: number | null;
  unidade_tributavel?: string | null;
  valor_total_tributos?: number | null;
  aliquota_aproximada_tributos?: number | null;
  fonte_valor_total_tributos?: string | null;
  icms_base_calculo?: number | null;
  icms_aliquota?: number | null;
  icms_valor?: number | null;
  icms_modalidade_base_calculo?: string | null;
  pis_base_calculo?: number | null;
  pis_aliquota?: number | null;
  pis_valor?: number | null;
  cofins_base_calculo?: number | null;
  cofins_aliquota?: number | null;
  cofins_valor?: number | null;
  ibs_cbs_situacao_tributaria?: string | null;
  ibs_cbs_classificacao_tributaria?: string | null;
  ibs_cbs_base_calculo?: number | null;
  cbs_aliquota?: number | null;
  cbs_valor?: number | null;
  ibs_uf_aliquota?: number | null;
  ibs_uf_valor?: number | null;
  ibs_mun_aliquota?: number | null;
  ibs_mun_valor?: number | null;
  ibs_valor_total?: number | null;
  is_valor?: number | null;
}

export function useItemFiscalProfile(itemId?: string, enabled = true) {
  return useQuery<ItemFiscalProfile | null>({
    queryKey: ["item-fiscal-profile", itemId],
    enabled: enabled && !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("item_fiscal_profiles")
        .select("*")
        .eq("item_id", itemId)
        .maybeSingle();

      if (error) throw error;
      if (data) return data as ItemFiscalProfile;

      const { data: importLine, error: importLineError } = await supabase
        .from("item_xml_import_lines")
        .select("*")
        .eq("item_id", itemId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (importLineError) throw importLineError;
      if (!importLine) return null;

      return {
        id: `xml-import-line:${importLine.id}`,
        item_id: importLine.item_id,
        customer_id: "",
        gtin: importLine.gtin ?? importLine.gtin_tributavel ?? null,
        ncm: importLine.ncm ?? null,
        cest: importLine.cest ?? null,
        cfop: importLine.cfop ?? null,
        cfop_outside_state: null,
        origin_code: importLine.origin_code ?? null,
        icms_tax_situation: importLine.icms_tax_situation ?? null,
        pis_tax_situation: importLine.pis_tax_situation ?? null,
        cofins_tax_situation: importLine.cofins_tax_situation ?? null,
        ipi_tax_situation: importLine.ipi_tax_situation ?? null,
        ipi_enquadramento: importLine.ipi_enquadramento ?? null,
        ipi_base_calculo: importLine.ipi_base_calculo ?? null,
        ipi_aliquota: importLine.ipi_aliquota ?? null,
        ipi_valor: importLine.ipi_valor ?? null,
        nfse_service_list_item: null,
        nfse_service_code: null,
        nfse_cnae: null,
        nfse_iss_rate: null,
        nfse_service_description: null,
        nfse_natureza_operacao: null,
        nfse_regime_especial_tributacao: null,
        nfse_optante_simples_nacional: null,
        nfse_iss_retido: null,
        nfse_base_calculo: null,
        nfse_valor_iss: null,
        nfse_valor_deducoes: null,
        nfse_desconto_incondicionado: null,
        nfse_desconto_condicionado: null,
        nfse_valor_pis: null,
        nfse_valor_cofins: null,
        nfse_ibs_cbs_situacao_tributaria: null,
        nfse_ibs_cbs_classificacao_tributaria: null,
        nfse_ibs_cbs_base_calculo: null,
        nfse_ibs_uf_aliquota: null,
        nfse_ibs_mun_aliquota: null,
        nfse_cbs_aliquota: null,
        nfse_ibs_uf_valor: null,
        nfse_ibs_mun_valor: null,
        nfse_cbs_valor: null,
        created_at: importLine.created_at,
        updated_at: importLine.created_at,
        unidade_tributavel: importLine.unidade_tributavel ?? importLine.unit ?? null,
        valor_total_tributos: importLine.valor_total_tributos ?? null,
        aliquota_aproximada_tributos:
          typeof importLine.valor_total_tributos === "number" && typeof importLine.total_value === "number" && importLine.total_value > 0
            ? Number(((importLine.valor_total_tributos / importLine.total_value) * 100).toFixed(4))
            : null,
        fonte_valor_total_tributos: importLine.valor_total_tributos != null ? "imported_xml" : null,
        icms_base_calculo: importLine.icms_base_calculo ?? null,
        icms_aliquota: importLine.icms_aliquota ?? null,
        icms_valor: importLine.icms_valor ?? null,
        icms_modalidade_base_calculo: importLine.icms_modalidade_base_calculo ?? null,
        pis_base_calculo: importLine.pis_base_calculo ?? null,
        pis_aliquota: importLine.pis_aliquota ?? null,
        pis_valor: importLine.pis_valor ?? null,
        cofins_base_calculo: importLine.cofins_base_calculo ?? null,
        cofins_aliquota: importLine.cofins_aliquota ?? null,
        cofins_valor: importLine.cofins_valor ?? null,
        ibs_cbs_situacao_tributaria: importLine.ibs_cbs_situacao_tributaria ?? null,
        ibs_cbs_classificacao_tributaria: importLine.ibs_cbs_classificacao_tributaria ?? null,
        ibs_cbs_base_calculo: importLine.ibs_cbs_base_calculo ?? null,
        cbs_aliquota: importLine.cbs_aliquota ?? null,
        cbs_valor: importLine.cbs_valor ?? null,
        ibs_uf_aliquota: importLine.ibs_uf_aliquota ?? null,
        ibs_uf_valor: importLine.ibs_uf_valor ?? null,
        ibs_mun_aliquota: importLine.ibs_mun_aliquota ?? null,
        ibs_mun_valor: importLine.ibs_mun_valor ?? null,
        ibs_valor_total: importLine.ibs_valor_total ?? null,
        is_valor: importLine.is_valor ?? null,
      } as ItemFiscalProfile;
    },
  });
}

export function useUpsertItemFiscalProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ItemFiscalProfileInput) => {
      const customerId = await getCurrentClinicCustomerId();
      const payload = {
        ...input,
        customer_id: customerId,
      };

      const { data, error } = await supabase
        .from("item_fiscal_profiles")
        .upsert(payload, { onConflict: "item_id" })
        .select("*")
        .single();

      if (error) throw error;
      return data as ItemFiscalProfile;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["item-fiscal-profile", data.item_id] });
    },
    onError: (error: Error) => {
      toast.error(i18n.t("itemFiscalProfile.saveError", { ns: "erp", message: error.message }));
    },
  });
}
